import { useState } from "react";
import { useFirmwareUpdate } from "./useDevices";
import { Device } from "types/device.types";

interface DeviceUpdateManager {
  selectedDevices: Device[];
  updateType: "firmware" | "nipper";
  confirmDialogVisible: boolean;
  isUpdating: boolean;
  setSelectedDevices: (devices: Device[]) => void;
  openFirmwareUpdateDialog: () => void;
  openNipperUpdateDialog: () => void;
  hideConfirmDialog: () => void;
  executeUpdate: () => Promise<void>;
}

export const useDeviceUpdateManager = (
  onSuccess?: (message: string) => void,
  onError?: (message: string) => void
): DeviceUpdateManager => {
  const [selectedDevices, setSelectedDevices] = useState<Device[]>([]);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [updateType, setUpdateType] = useState<"firmware" | "nipper">(
    "firmware"
  );
  const firmwareUpdateMutation = useFirmwareUpdate();

  const openFirmwareUpdateDialog = () => {
    if (!selectedDevices.length) {
      onError?.("Please select at least one device to update.");
      return;
    }
    setUpdateType("firmware");
    setConfirmDialogVisible(true);
  };

  const openNipperUpdateDialog = () => {
    if (!selectedDevices.length) {
      onError?.("Please select at least one device to update.");
      return;
    }
    setUpdateType("nipper");
    setConfirmDialogVisible(true);
  };

  const hideConfirmDialog = () => {
    setConfirmDialogVisible(false);
  };

  const executeUpdate = async () => {
    try {
      // Map selected devices to their serial numbers
      const serialNumbers = selectedDevices.map(
        (device) => device.serialNumber
      );

      // Execute the update
      await firmwareUpdateMutation.mutateAsync({
        product: updateType === "firmware" ? "lifeguard" : "nipper",
        serialNumbers,
      });

      onSuccess?.(
        `Update initiated for ${serialNumbers.length} device${
          serialNumbers.length > 1 ? "s" : ""
        }.`
      );
    } catch (error) {
      console.error("Error updating devices:", error);
      onError?.("Failed to update devices. Please try again.");
    } finally {
      setConfirmDialogVisible(false);
    }
  };

  return {
    selectedDevices,
    updateType,
    confirmDialogVisible,
    isUpdating: firmwareUpdateMutation.isPending,
    setSelectedDevices,
    openFirmwareUpdateDialog,
    openNipperUpdateDialog,
    hideConfirmDialog,
    executeUpdate,
  };
};
